import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingBag, RefreshCw, Unplug, Info, Plus } from "lucide-react";
import { useEtsyIntegration } from "@/hooks/useEtsyIntegration";
import { useEtsyStore } from "@/hooks/useEtsyStore";

interface EtsyIntegrationCardProps {
  isConnected: boolean;
  onConnectionChange: (connected: boolean) => void;
}

const EtsyIntegrationCard = ({ isConnected, onConnectionChange }: EtsyIntegrationCardProps) => {
  const { 
    connectEtsy, 
    disconnectEtsy,
    isConnecting, 
    isDisconnecting
  } = useEtsyIntegration();

  const {
    hasActiveSubscription,
    isSubscriptionLoading,
    isFetchingOrders,
    isFetchingProducts,
    hasMoreOrders,
    hasMoreProducts,
    isLoadingMoreOrders,
    isLoadingMoreProducts,
    fetchOrders,
    fetchProducts,
    loadMoreOrders,
    loadMoreProducts,
  } = useEtsyStore();

  const handleDisconnect = async () => {
    await disconnectEtsy();
    onConnectionChange(false);
  };

  const handleFullSync = async () => {
    try {
      // Sync orders and products from Etsy API (force refresh)
      await Promise.all([
        fetchOrders(true),
        fetchProducts(true)
      ]);
    } catch (error) {
      console.error('Error during full sync:', error);
    }
  };

  const isSyncing = isFetchingOrders || isFetchingProducts;
  const isLoadingMore = isLoadingMoreOrders || isLoadingMoreProducts;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingBag className="h-5 w-5" />
          Etsy Integration
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Etsy Attribution Notice - Prominently displayed */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <Info className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-orange-800">
            The term 'Etsy' is a trademark of Etsy, Inc. This application uses the Etsy API but is not endorsed or certified by Etsy, Inc.
          </p>
        </div>

        {!hasActiveSubscription && !isSubscriptionLoading ? (
          <p className="text-gray-600">You need an active subscription to connect your Etsy shop.</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              {isConnected ? (
                <>
                  <div className="flex items-center gap-2 text-green-700">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Connected to Etsy</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      onClick={handleFullSync}
                      disabled={isSyncing || isLoadingMore}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <RefreshCw className={`mr-2 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                      {isSyncing ? 'Syncing...' : 'Sync Data'}
                    </Button>
                    <Button 
                      onClick={handleDisconnect}
                      disabled={isDisconnecting}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Unplug className={`mr-2 h-4 w-4 ${isDisconnecting ? 'animate-spin' : ''}`} />
                      {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-600">Connect your Etsy shop to automatically import your orders and products.</p>
                  <Button 
                    onClick={connectEtsy}
                    disabled={isConnecting}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <ShoppingBag className="mr-2 h-4 w-4" />
                    {isConnecting ? 'Connecting...' : 'Connect Etsy'}
                  </Button>
                </>
              )}
            </div>

            {/* Load More Buttons */}
            {isConnected && (hasMoreOrders || hasMoreProducts) && (
              <div className="flex gap-2 flex-wrap">
                {hasMoreOrders && (
                  <Button 
                    onClick={loadMoreOrders}
                    disabled={isLoadingMoreOrders || isSyncing}
                    size="sm"
                    variant="outline"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    <Plus className={`mr-2 h-4 w-4 ${isLoadingMoreOrders ? 'animate-spin' : ''}`} />
                    {isLoadingMoreOrders ? 'Loading...' : 'Load More Orders'}
                  </Button>
                )}
                {hasMoreProducts && (
                  <Button 
                    onClick={loadMoreProducts}
                    disabled={isLoadingMoreProducts || isSyncing}
                    size="sm"
                    variant="outline"
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  >
                    <Plus className={`mr-2 h-4 w-4 ${isLoadingMoreProducts ? 'animate-spin' : ''}`} />
                    {isLoadingMoreProducts ? 'Loading...' : 'Load More Products'}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
        {hasActiveSubscription && !isSubscriptionLoading && isConnected && (
          <div className="mt-4 text-sm text-gray-600">
            <p><strong>Note:</strong> Data is fetched in batches of 100 items. Use "Load More" buttons to fetch additional items, or "Sync Data" to refresh from Etsy.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EtsyIntegrationCard;
